import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import qs from 'query-string';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
//
import { filtersMeta, filtersMetaSearch, filtersMetaAside, filtersMetaNameDate } from './filtersMeta';
import { useAppConfig } from '@state';
import { useDebounce, useSearchParams } from '../../hooks';
import { utils, Types as coreTypes } from '@ohif/core';

import {
  StudyListExpandedRow,
  EmptyStudies,
  StudyListTable,
  StudyListPagination,
  StudyListFilter,
  StudyListFilterAside,
  Button,
  ButtonEnums,
} from '@ohif/ui';

import {
  Header,
  Icons,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Clipboard,
  useModal,
  useSessionStorage,
  Onboarding,
  ScrollArea,
  InvestigationalUseDialog,
} from '@ohif/ui-next';

import { Types } from '@ohif/ui';

import { preserveQueryParameters, preserveQueryStrings } from '../../utils/preserveQueryParameters';
('./filtersMeta.js');

const PatientInfoVisibility = Types.PatientInfoVisibility;

const { sortBySeriesDate } = utils;

const seriesInStudiesMap = new Map();

/**
 * TODO:
 * - debounce `setFilterValues` (150ms?)
 */
function WorkList({
  data: studies,
  dataTotal: studiesTotal,
  isLoadingData,
  dataSource,
  hotkeysManager,
  dataPath,
  onRefresh,
  servicesManager,
}: withAppTypes) {
  const { show, hide } = useModal();
  const { t } = useTranslation();
  // ~ Modes
  const [appConfig] = useAppConfig();
  // ~ Filters
  const searchParams = useSearchParams();
  const navigate = useNavigate();
  const STUDIES_LIMIT = 2001;
  const queryFilterValues = _getQueryFilterValues(searchParams);
  const [sessionQueryFilterValues, updateSessionQueryFilterValues] = useSessionStorage({
    key: 'queryFilterValues',
    defaultValue: queryFilterValues,
    // ToDo: useSessionStorage currently uses an unload listener to clear the filters from session storage
    // so on systems that do not support unload events a user will NOT be able to alter any existing filter
    // in the URL, load the page and have it apply.
    clearOnUnload: true,
  });

  const [filterValues, _setFilterValues] = useState({
    ...defaultFilterValues,
    ...sessionQueryFilterValues,
  });

  const [iframeModalOpen, setIframeModalOpen] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const debouncedFilterValues = useDebounce(filterValues, 200);
  const { resultsPerPage, pageNumber, sortBy, sortDirection } = filterValues;

  /*
   * The default sort value keep the filters synchronized with runtime conditional sorting
   * Only applied if no other sorting is specified and there are less than 2001 studies
   */

  const canSort = Number(studiesTotal) < STUDIES_LIMIT;
  const shouldUseDefaultSort = sortBy === '' || !sortBy;
  const sortModifier = sortDirection === 'ascending' ? 1 : -1;
  const defaultSortValues =
    shouldUseDefaultSort && canSort ? { sortBy: 'studyDate', sortDirection: 'ascending' } : {};
  const { customizationService } = servicesManager.services;

  const sortedStudies = useMemo(() => {
    if (!canSort) {
      return studies;
    }
    if (!Array.isArray(studies)) {
      return;
    }
    return [...studies].sort((s1, s2) => {
      if (shouldUseDefaultSort) {
        const descendingSortModifier = 1;
        return _sortStringDates(s1, s2, descendingSortModifier);
      }

      const s1Prop = s1[sortBy];
      const s2Prop = s2[sortBy];

      if (typeof s1Prop === 'string' && typeof s2Prop === 'string') {
        return s1Prop.localeCompare(s2Prop) * sortModifier;
      } else if (typeof s1Prop === 'number' && typeof s2Prop === 'number') {
        return (s1Prop > s2Prop ? 1 : -1) * sortModifier;
      } else if (!s1Prop && s2Prop) {
        return -1 * sortModifier;
      } else if (!s2Prop && s1Prop) {
        return 1 * sortModifier;
      } else if (sortBy === 'studyDate') {
        return _sortStringDates(s1, s2, sortModifier);
      }

      return 0;
    });
  }, [canSort, studies, shouldUseDefaultSort, sortBy, sortModifier]);

  // ~ Rows & Studies
  const [expandedRows, setExpandedRows] = useState([]);
  const [studiesWithSeriesData, setStudiesWithSeriesData] = useState([]);
  const numOfStudies = studiesTotal;
  const querying = useMemo(() => {
    return isLoadingData || expandedRows.length > 0;
  }, [isLoadingData, expandedRows]);

  const setFilterValues = (val: any) => {
    if (filterValues.pageNumber === val.pageNumber) {
      val.pageNumber = 1;
    }
    _setFilterValues(val);
    updateSessionQueryFilterValues(val);
    setExpandedRows([]);
  };

  const onPageNumberChange = (newPageNumber: any) => {
    const oldPageNumber: number = filterValues.pageNumber;
    const rollingPageNumberMod: number = Math.floor(2001 / filterValues.resultsPerPage);
    const rollingPageNumber: number = oldPageNumber % rollingPageNumberMod;
    const isNextPage: boolean = newPageNumber > oldPageNumber;
    const hasNextPage: boolean =
      Math.max(rollingPageNumber, 1) * Number(resultsPerPage) < Number(numOfStudies);

    if (isNextPage && !hasNextPage) {
      return;
    }

    setFilterValues({ ...filterValues, pageNumber: newPageNumber });
  };

  const onResultsPerPageChange = newResultsPerPage => {
    setFilterValues({
      ...filterValues,
      pageNumber: 1,
      resultsPerPage: Number(newResultsPerPage),
    });
  };

  // Set body style
  useEffect(() => {
    document.body.classList.add('bg-black');
    return () => {
      document.body.classList.remove('bg-black');
    };
  }, []);

  // Sync URL query parameters with filters
  useEffect(() => {
    if (!debouncedFilterValues) {
      return;
    }

    const queryString: any = {};
    Object.keys(defaultFilterValues).forEach(key => {
      const defaultValue = defaultFilterValues[key];
      const currValue = debouncedFilterValues[key];

      // TODO: nesting/recursion?
      if (key === 'studyDate') {
        if (currValue.startDate && defaultValue.startDate !== currValue.startDate) {
          queryString.startDate = currValue.startDate;
        }
        if (currValue.endDate && defaultValue.endDate !== currValue.endDate) {
          queryString.endDate = currValue.endDate;
        }
      } else if (key === 'modalities' && currValue.length) {
        queryString.modalities = currValue.join(',');
      } else if (currValue !== defaultValue) {
        queryString[key] = currValue;
      }
    });

    preserveQueryStrings(queryString);

    const search = qs.stringify(queryString, {
      skipNull: true,
      skipEmptyString: true,
    });
    navigate({
      pathname: '/',
      search: search ? `?${search}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilterValues]);

  // Query for series information
  useEffect(() => {
    const fetchSeries = async (studyInstanceUid: any) => {
      try {
        const series = await dataSource.query.series.search(studyInstanceUid);
        seriesInStudiesMap.set(studyInstanceUid, sortBySeriesDate(series));
        setStudiesWithSeriesData([...studiesWithSeriesData, studyInstanceUid]);
      } catch (ex) {
        // TODO: UI Notification Service
        console.warn(ex);
      }
    };

    // TODO: WHY WOULD YOU USE AN INDEX OF 1?!
    // Note: expanded rows index begins at 1
    for (let z = 0; z < expandedRows.length; z++) {
      const expandedRowIndex = expandedRows[z] - 1;
      const studyInstanceUid = sortedStudies[expandedRowIndex].studyInstanceUid;

      if (studiesWithSeriesData.includes(studyInstanceUid)) {
        continue;
      }

      fetchSeries(studyInstanceUid);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRows, studies]);

  const isFiltering = (filterValues, defaultFilterValues) => {
    return !isEqual(filterValues, defaultFilterValues);
  };

  const rollingPageNumberMod = Math.floor(2001 / resultsPerPage);
  const rollingPageNumber = (pageNumber - 1) % rollingPageNumberMod;
  const offset = resultsPerPage * rollingPageNumber;
  const offsetAndTake = offset + resultsPerPage;

  const tableDataSource = (sortedStudies as any).map((study: any, key: any) => {
    const rowKey = key + 1;
    const isExpanded = expandedRows.some(k => k === rowKey);
    const {
      studyInstanceUid,
      accession,
      modalities,
      instances,
      description,
      mrn,
      patientName,
      date,
      time,
      institutionName,
    } = study;
    const studyDate =
      date &&
      moment(date, ['YYYYMMDD', 'YYYY.MM.DD'], true).isValid() &&
      moment(date, ['YYYYMMDD', 'YYYY.MM.DD']).format(t('Common:localDateFormat', 'MMM-DD-YYYY'));
    const studyTime =
      time &&
      moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).isValid() &&
      moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).format(
        t('Common:localTimeFormat', 'hh:mm A')
      );

    const makeCopyTooltipCell = textValue => {
      if (!textValue) {
        return '';
      }
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-pointer truncate">{textValue}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <div className="flex items-center justify-between gap-2">
              {textValue}
              <Clipboard>{textValue}</Clipboard>
            </div>
          </TooltipContent>
        </Tooltip>
      );
    };
    const seriesColumn = {
      description: t('StudyList:Description'),
      seriesNumber: t('StudyList:Series'),
      modality: t('StudyList:Modality'),
      instances: t('StudyList:Instances'),
    };
    const seriesStudy = seriesInStudiesMap.has(studyInstanceUid)
      ? seriesInStudiesMap.get(studyInstanceUid).map((s: any) => {
        return {
          description: s.description || '(empty)',
          seriesNumber: s.seriesNumber ?? '',
          modality: s.modality || '',
          instances: s.numSeriesInstances || '',
        };
      })
      : [];
    return {
      dataCY: `studyRow-${studyInstanceUid}`,
      clickableCY: studyInstanceUid,
      row: [
        {
          key: 'instanceUid',
          content: studyInstanceUid ?? '',
          gridCol: 4,
        },
        {
          key: 'institutionName',
          content: makeCopyTooltipCell(institutionName),
          gridCol: 4,
        },
        {
          key: 'patientName',
          content: patientName ?? '',
          gridCol: 4,
        },
        {
          key: 'seriesColumn',
          content: seriesColumn,
          gridCol: 4,
        },
        {
          key: 'seriesStudy',
          content: seriesStudy,
          gridCol: 4,
        },
        {
          key: 'mrn',
          content: mrn ?? '',
          gridCol: 3,
        },
        {
          key: 'studyDate',
          content: (
            <>
              {studyDate && <span className="mr-4">{studyDate}</span>}
              {studyTime && <span>{studyTime}</span>}
            </>
          ),
          title: `${studyDate || ''} ${studyTime || ''}`,
          gridCol: 5,
        },
        {
          key: 'description',
          content: makeCopyTooltipCell(description),
          gridCol: 4,
        },
        {
          key: 'modality',
          content: modalities,
          title: modalities,
          gridCol: 3,
        },
        {
          key: 'accession',
          content: makeCopyTooltipCell(accession),
          gridCol: 3,
        },
        {
          key: 'instances',
          content: (
            <>
              <Icons.GroupLayers
                className={classnames('mr-2 inline-flex w-4', {
                  'text-primary': isExpanded,
                  'text-secondary-light': !isExpanded,
                })}
              />
              {instances}
            </>
          ),
          title: (instances || 0).toString(),
          gridCol: 2,
        },
      ],
      // Todo: This is actually running for all rows, even if they are
      // not clicked on.
      // expandedContent: (
      //   <StudyListExpandedRow
      //     seriesTableColumns={seriesColumn}
      //     seriesTableDataSource={seriesStudy}
      //   >
      //     <div className="flex gap-2 max-sm:grid max-sm:grid-cols-2">
      //       {(appConfig.groupEnabledModesFirst
      //         ? appConfig.loadedModes.sort((a, b) => {
      //             const isValidA = a.isValidMode({
      //               modalities: modalities.replaceAll('/', '\\'),
      //               study,
      //             }).valid;
      //             const isValidB = b.isValidMode({
      //               modalities: modalities.replaceAll('/', '\\'),
      //               study,
      //             }).valid;

      //             return isValidB - isValidA;
      //           })
      //         : appConfig.loadedModes
      //       ).map((mode, i) => {
      //         const modalitiesToCheck = modalities.replaceAll('/', '\\');

      //         const { valid: isValidMode, description: invalidModeDescription } = mode.isValidMode({
      //           modalities: modalitiesToCheck,
      //           study,
      //         });
      //         // TODO: Modes need a default/target route? We mostly support a single one for now.
      //         // We should also be using the route path, but currently are not
      //         // mode.routeName
      //         // mode.routes[x].path
      //         // Don't specify default data source, and it should just be picked up... (this may not currently be the case)
      //         // How do we know which params to pass? Today, it's just StudyInstanceUIDs and configUrl if exists
      //         const query = new URLSearchParams();
      //         if (filterValues.configUrl) {
      //           query.append('configUrl', filterValues.configUrl);
      //         }
      //         query.append('StudyInstanceUIDs', studyInstanceUid);
      //         preserveQueryParameters(query);

      //         return (
      //           mode.displayName && (
      //             <Link
      //               className={`${isValidMode ? '' : 'cursor-not-allowed'} ' hidden'`}
      //               key={i}
      //               to={`${mode.routeName}${dataPath || ''}?${query.toString()}`}
      //               onClick={event => {
      //                 // In case any event bubbles up for an invalid mode, prevent the navigation.
      //                 // For example, the event bubbles up when the icon embedded in the disabled button is clicked.
      //                 if (!isValidMode) {
      //                   event.preventDefault();
      //                 }
      //               }}
      //               // to={`${mode.routeName}/dicomweb?StudyInstanceUIDs=${studyInstanceUid}`}
      //             >
      //               {/* TODO revisit the completely rounded style of buttons used for launching a mode from the worklist later */}
      //               <Button
      //                 type={ButtonEnums.type.primary}
      //                 size={ButtonEnums.size.medium}
      //                 disabled={!isValidMode}
      //                 startIconTooltip={
      //                   !isValidMode ? (
      //                     <div className="font-inter mx-2 flex w-[206px] whitespace-normal break-words text-left text-xs font-normal text-white">
      //                       {invalidModeDescription}
      //                     </div>
      //                   ) : null
      //                 }
      //                 startIcon={
      //                   isValidMode ? (
      //                     <Icons.LaunchArrow className="!h-[20px] !w-[20px] text-black" />
      //                   ) : (
      //                     <Icons.LaunchInfo className="!h-[20px] !w-[20px] text-black" />
      //                   )
      //                 }
      //                 onClick={() => {}}
      //                 dataCY={`mode-${mode.routeName}-${studyInstanceUid}`}
      //                 className={isValidMode ? 'text-[13px]' : 'hidden bg-[#222d44] text-[13px]'}
      //               >
      //                 {mode.displayName}
      //               </Button>
      //             </Link>
      //           )
      //         );
      //       })}
      //     </div>
      //   </StudyListExpandedRow>
      // ),
      // onClickRow: () =>
      //   setExpandedRows(s => (isExpanded ? s.filter(n => rowKey !== n) : [...s, rowKey])),
      // isExpanded,
    };
  });
  const hasStudies = Number(numOfStudies) > 0;

  const AboutModal = customizationService.getCustomization(
    'ohif.aboutModal'
  ) as coreTypes.MenuComponentCustomization;
  const UserPreferencesModal = customizationService.getCustomization(
    'ohif.userPreferencesModal'
  ) as coreTypes.MenuComponentCustomization;

  const menuOptions = [
    {
      title: AboutModal?.menuTitle ?? t('Header:About'),
      icon: 'info',
      onClick: () =>
        show({
          content: AboutModal,
          title: AboutModal?.title ?? t('AboutModal:ABOUT MED Viewer'),
          containerClassName: AboutModal?.containerClassName ?? 'max-w-md',
        }),
    },
    {
      title: UserPreferencesModal.menuTitle ?? t('Header:Preferences'),
      icon: 'settings',
      onClick: () =>
        show({
          content: UserPreferencesModal as React.ComponentType,
          title: UserPreferencesModal.title ?? t('UserPreferencesModal:User preferences'),
          containerClassName:
            UserPreferencesModal?.containerClassName ?? 'flex max-w-4xl p-6 flex-col',
        }),
    },
  ];

  if (appConfig.oidc) {
    menuOptions.push({
      icon: 'power-off',
      title: t('Header:Logout'),
      onClick: () => {
        navigate(`/logout?redirect_uri=${encodeURIComponent(window.location.href)}`);
      },
    });
  }

  const LoadingIndicatorProgress = customizationService.getCustomization(
    'ui.loadingIndicatorProgress'
  );
  const DicomUploadComponent = customizationService.getCustomization('dicomUploadComponent');

  const uploadProps =
    DicomUploadComponent && (dataSource as any).getConfig()?.dicomUploadEnabled
      ? {
        title: 'Subir archivos DICOM',
        closeButton: true,
        shouldCloseOnEsc: true,
        shouldCloseOnOverlayClick: true,
        content: () => (
          <DicomUploadComponent
            dataSource={dataSource}
            onComplete={() => {
              hide();
              onRefresh();
            }}
            onStarted={() => {
              show({
                ...uploadProps,
                // when upload starts, hide the default close button as closing the dialogue must be handled by the upload dialogue itself
                closeButton: true,
              });
            }}
          />
        ),
      }
      : undefined;

  const dataSourceConfigurationComponent = customizationService.getCustomization(
    'ohif.dataSourceConfigurationComponent'
  );

  return (
    <div className="flex h-screen flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
        showPatientInfo={PatientInfoVisibility.VISIBLE}
      />
      <Onboarding />
      <InvestigationalUseDialog dialogConfiguration={appConfig?.investigationalUseDialog} />
      <div className="flex h-full w-full flex-col overflow-y-auto sm:pl-10">
        <ScrollArea>
          <div className="flex grow flex-col px-2 sm:px-4">
            <StudyListFilter
              numOfStudies={pageNumber * resultsPerPage > 2000 ? 2001 : numOfStudies}
              filtersMeta={filtersMetaNameDate}
              allFiltersMeta={filtersMetaNameDate}
              filterValues={{ ...filterValues, ...defaultSortValues }}
              onChange={setFilterValues}
              clearFilters={() => setFilterValues(defaultFilterValues)}
              isFiltering={isFiltering(filterValues, defaultFilterValues)}
              onUploadClick={uploadProps ? () => show(uploadProps) : undefined}
              getDataSourceConfigurationComponent={
                dataSourceConfigurationComponent
                  ? () => dataSourceConfigurationComponent()
                  : undefined
              }
            />
            <StudyListFilterAside
              numOfStudies={Number(pageNumber * resultsPerPage > 2000 ? 2001 : numOfStudies)}
              filtersMeta={filtersMetaAside}
              allFiltersMeta={filtersMetaAside}
              filterValues={{ ...filterValues, ...defaultSortValues }}
              onChange={setFilterValues}
              clearFilters={() => setFilterValues(defaultFilterValues)}
              isFiltering={isFiltering(filterValues, defaultFilterValues)}
              onUploadClick={uploadProps ? () => show(uploadProps) : undefined}
              getDataSourceConfigurationComponent={
                dataSourceConfigurationComponent
                  ? () => dataSourceConfigurationComponent()
                  : undefined
              }
            />
          </div>
          {hasStudies ? (
            <div className="flex grow flex-col px-2 sm:px-4">
              <StudyListTable
                tableDataSource={tableDataSource.slice(offset, offsetAndTake)}
                numOfStudies={Number(numOfStudies) || 0}
                querying={querying}
                filtersMeta={filtersMeta}
                dataSource={dataSource}
              />
              <div className="grow">
                <StudyListPagination
                  onChangePage={onPageNumberChange}
                  onChangePerPage={onResultsPerPageChange}
                  currentPage={pageNumber}
                  perPage={resultsPerPage}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center px-4 pt-24">
              {appConfig.showLoadingIndicator && isLoadingData ? (
                <LoadingIndicatorProgress className="h-full w-full bg-black" />
              ) : (
                <EmptyStudies />
              )}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Floating Button – solo visible si NO hay HTTPS */}
      {window.location.protocol !== 'https:' && (
        <button
          onClick={() => { setIframeLoaded(false); setIframeModalOpen(true); }}
          className="fixed bottom-8 right-8 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-primary-main text-white shadow-lg transition-transform hover:scale-110 hover:bg-primary-dark"
          title="Abrir aplicación en la nube"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
        </button>
      )}

      {/* Iframe Modal Portal – solo visible si NO hay HTTPS */}
      {window.location.protocol !== 'https:' && iframeModalOpen && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 99999,
            display: 'flex',
            flexDirection: 'column',
            background: '#111',
            overflow: 'hidden',
          }}
        >
          {/* Botón flotante para cerrar – superpuesto sobre el iframe */}
          <button
            onClick={() => setIframeModalOpen(false)}
            className="bg-primary-main focus:bg-primary-dark fixed bottom-8 right-8 z-[100000] flex h-16 w-16 items-center justify-center rounded-full bg-rose-700 text-white shadow-lg transition-transform hover:scale-110 hover:bg-rose-600"
            title="Cerrar aplicación"
          >
            {/* Ícono de servidor */}
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
          </button>

          {/* Loading overlay */}
          {!iframeLoaded && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: '#0f0f0f',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}>
              <div style={{
                width: 56,
                height: 56,
                border: '5px solid rgba(255,255,255,0.15)',
                borderTop: '5px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 0.9s linear infinite',
              }} />
              <p style={{ color: '#aaa', marginTop: 16, fontSize: 14 }}>Cargando...</p>
              <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
            </div>
          )}
          <iframe
            src="https://server5.signa-engineering.com.bo"
            title="Visor Externo"
            onLoad={() => setIframeLoaded(true)}
            style={{
              width: '100%',
              height: '100vh',
              border: 'none',
              display: 'block',
            }}
          />
        </div>,
        document.body
      )}

    </div>
  );
}

WorkList.propTypes = {
  data: PropTypes.array.isRequired,
  dataSource: PropTypes.shape({
    query: PropTypes.object.isRequired,
    getConfig: PropTypes.func,
  }).isRequired,
  isLoadingData: PropTypes.bool.isRequired,
  servicesManager: PropTypes.object.isRequired,
};

const defaultFilterValues = {
  patientName: '',
  mrn: '',
  studyDate: {
    startDate: null,
    endDate: null,
  },
  description: '',
  modalities: [],
  accession: '',
  sortBy: 'studyDate',
  sortDirection: 'ascending',
  pageNumber: 1,
  resultsPerPage: 25,
  datasources: '',
};

function _tryParseInt(str, defaultValue) {
  let retValue = defaultValue;
  if (str && str.length > 0) {
    if (!isNaN(str)) {
      retValue = parseInt(str);
    }
  }
  return retValue;
}

function _getQueryFilterValues(params) {
  const newParams = new URLSearchParams();
  for (const [key, value] of params) {
    newParams.set(key.toLowerCase(), value);
  }
  params = newParams;

  const queryFilterValues = {
    patientName: params.get('patientname'),
    mrn: params.get('mrn'),
    studyDate: {
      startDate: params.get('startdate') || null,
      endDate: params.get('enddate') || null,
    },
    description: params.get('description'),
    modalities: params.get('modalities') ? params.get('modalities').split(',') : [],
    accession: params.get('accession'),
    sortBy: params.get('sortby'),
    sortDirection: params.get('sortdirection'),
    pageNumber: _tryParseInt(params.get('pagenumber'), undefined),
    resultsPerPage: _tryParseInt(params.get('resultsperpage'), undefined),
    datasources: params.get('datasources'),
    configUrl: params.get('configurl'),
  };

  // Delete null/undefined keys
  Object.keys(queryFilterValues).forEach(
    key => queryFilterValues[key] == null && delete queryFilterValues[key]
  );

  return queryFilterValues;
}

function _sortStringDates(s1, s2, sortModifier) {
  // TODO: Delimiters are non-standard. Should we support them?
  const s1Date = moment(s1.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);
  const s2Date = moment(s2.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);

  if (s1Date.isValid() && s2Date.isValid()) {
    return (s1Date.toISOString() > s2Date.toISOString() ? 1 : -1) * sortModifier;
  } else if (s1Date.isValid()) {
    return sortModifier;
  } else if (s2Date.isValid()) {
    return -1 * sortModifier;
  }
}

export default WorkList;
